import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiFileText, FiPrinter, FiRefreshCw, FiSearch } from 'react-icons/fi';
import api from '../../services/api';
import PaymentModal from '../PaymentModal';

function ymd(d) {
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
}

function startOfMonthYmd() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  first.setHours(0, 0, 0, 0);
  return ymd(first);
}

function last7FromYmd() {
  const dt = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
  return ymd(dt);
}

function todayYmd() {
  return ymd(new Date());
}

export default function BillingHistoryPanel({
  customerId,
  refreshKey,
  onPrintPosBill,
  styles = {}
}) {
  const [range, setRange] = useState('last7'); // today | last7 | month | custom
  const [onlyCustomer, setOnlyCustomer] = useState(false);
  const [fromDate, setFromDate] = useState(() => last7FromYmd());
  const [toDate, setToDate] = useState(() => todayYmd());
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [payModal, setPayModal] = useState({ open: false, id: null });
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); // pdf:<id>
  const [companyUsersById, setCompanyUsersById] = useState({});

  // If customer is cleared (Walk-in), also clear the customer-only filter.
  // This prevents "Only this customer" from staying true while disabled.
  useEffect(() => {
    if (!customerId && onlyCustomer) setOnlyCustomer(false);
  }, [customerId, onlyCustomer]);

  // Load company users once, so we can map createdBy (id) -> user name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/companies/users');
        const users = Array.isArray(data?.users) ? data.users : [];
        const map = {};
        for (const u of users) {
          if (u?.id == null) continue;
          const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
          map[String(u.id)] = fullName || u.email || `User #${u.id}`;
        }
        if (!cancelled) setCompanyUsersById(map);
      } catch (_) {
        // Non-critical; we fall back to createdByUser or the raw id.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync date inputs when range changes (unless custom).
  useEffect(() => {
    if (range === 'today') {
      const t = todayYmd();
      setFromDate(t);
      setToDate(t);
    } else if (range === 'last7') {
      setFromDate(last7FromYmd());
      setToDate(todayYmd());
    } else if (range === 'month') {
      setFromDate(startOfMonthYmd());
      setToDate(todayYmd());
    }
  }, [range]);

  const queryParams = useMemo(() => {
    const p = {
      page,
      pageSize,
      createdFrom: 'POS',
      fromDate,
      toDate
    };
    if (onlyCustomer && customerId) p.customerId = customerId;
    return p;
  }, [page, pageSize, fromDate, toDate, onlyCustomer, customerId]);

  const fetchPage = async ({ reset = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/invoices', { params: queryParams });
      const list = Array.isArray(data?.data) ? data.data : [];
      const tot = Number(data?.total || 0);
      setTotal(tot);
      setRows((prev) => (reset ? list : [...prev, ...list]));
    } catch (e) {
      console.error('Failed to load billing history', e);
      setError('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  // Reset paging when filters change or parent asks refresh.
  useEffect(() => {
    setPage(1);
    setRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, fromDate, toDate, onlyCustomer, onlyCustomer ? customerId : null, refreshKey]);

  // Fetch when page changes (and after resets).
  useEffect(() => {
    fetchPage({ reset: page === 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryParams.createdFrom, queryParams.fromDate, queryParams.toDate, queryParams.customerId, refreshKey]);

  const filteredRows = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return rows;
    return (rows || []).filter((inv) => {
      const num = String(inv?.number || '').toLowerCase();
      const name = String(inv?.customerSnapshot?.name || '').toLowerCase();
      const phone = String(inv?.customerSnapshot?.phone || inv?.customerSnapshot?.mobile || '').toLowerCase();
      return num.includes(q) || name.includes(q) || phone.includes(q);
    });
  }, [rows, search]);

  const hasMore = rows.length < total;

  const formatINR = (val) => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const openPdf = async (inv) => {
    const id = inv?.id;
    if (!id) return;
    const key = `pdf:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.get(`/api/invoices/${id}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error('Failed to open PDF', e);
      if (window.toast?.call) window.toast('Failed to open PDF');
    } finally {
      setActionLoading((s) => {
        const n = { ...s };
        delete n[key];
        return n;
      });
    }
  };

  const submitMarkPaid = async (paymentData) => {
    if (!payModal.id) return;
    setPaySubmitting(true);
    try {
      await api.post(`/api/invoices/${payModal.id}/mark-paid`, paymentData);
      if (window.toast?.call) window.toast('Invoice marked as paid');
      setPayModal({ open: false, id: null });
      // Refresh first page for updated status/timestamps
      setPage(1);
      setRows([]);
    } catch (e) {
      console.error('Failed to mark paid', e);
      if (window.toast?.call) window.toast('Failed to mark as paid');
    } finally {
      setPaySubmitting(false);
    }
  };

  const ui = {
    panel: {
      marginTop: 12,
      borderTop: '1px dashed #e5e7eb',
      paddingTop: 12,
      ...styles.panel
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 8
    },
    title: { fontWeight: 700, fontSize: 13, color: '#111827' },
    controlsRow: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      flexWrap: 'wrap',
      marginBottom: 8
    },
    select: {
      fontSize: 12,
      padding: '6px 8px',
      height: 32,
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      background: '#fff'
    },
    input: {
      fontSize: 12,
      padding: '6px 8px',
      height: 32,
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      background: '#fff'
    },
    checkbox: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' },
    list: {
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      overflow: 'hidden',
      background: '#fff'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 8,
      padding: '8px 10px',
      borderBottom: '1px solid #f3f4f6'
    },
    rowLeft: { minWidth: 0 },
    rowRight: { display: 'inline-flex', gap: 6, alignItems: 'center' },
    num: { fontSize: 12, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    meta: { fontSize: 11, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    amount: { fontSize: 12, fontWeight: 500, color: '#111827', textAlign: 'right' },
    badge: (status) => {
      const s = String(status || '').toUpperCase();
      const map = {
        PAID: { bg: '#ecfdf5', fg: '#047857' },
        OVERDUE: { bg: '#fff1f2', fg: '#be123c' },
        SENT: { bg: '#f5f3ff', fg: '#6d28d9' },
        APPROVED: { bg: '#ecfeff', fg: '#0e7490' },
        DRAFT: { bg: '#f5f5f4', fg: '#57534e' }
      };
      const c = map[s] || { bg: '#f5f5f4', fg: '#57534e' };
      return {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        textTransform: 'uppercase',
        letterSpacing: '0.03em'
      };
    },
    iconBtn: {
      /* width: 28,
      height: 28, */
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      border: '1px solid #e5e7eb',
      background: '#fff',
      cursor: 'pointer',
      color: '#374151',
      lineHeight: 0
    },
    footer: { marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
    smallText: { fontSize: 11, color: '#6b7280' }
  };

  return (
    <div style={ui.panel}>
      <div style={ui.headerRow}>
        <div style={ui.title}>Billing History</div>
        <button
          type="button"
          style={ui.iconBtn}
          title="Refresh"
          onClick={() => { setPage(1); setRows([]); }}
          disabled={loading}
        >
          <FiRefreshCw size={14} color="#374151" stroke="#374151" style={{ display: 'block' }} />
        </button>
      </div>

      <div style={ui.controlsRow}>
        <select style={ui.select} value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="today">Today</option>
          <option value="last7">Last 7 days</option>
          <option value="month">This month</option>
          <option value="custom">Custom</option>
        </select>

        {range === 'custom' && (
          <>
            <input style={ui.input} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <input style={ui.input} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </>
        )}

        <label style={ui.checkbox} title={customerId ? '' : 'Select a customer to enable'}>
          <input
            type="checkbox"
            checked={onlyCustomer}
            onChange={(e) => setOnlyCustomer(e.target.checked)}
            disabled={!customerId}
          />
          Only this customer
        </label>

        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <FiSearch
            size={14}
            color="#9ca3af"
            stroke="#9ca3af"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'block'
            }}
          />
          <input
            style={{ ...ui.input, width: '100%', paddingLeft: 30 }}
            placeholder="Search bill…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div style={ui.list}>
        {filteredRows.map((inv, idx) => (
          <div key={inv.id || idx} style={{ ...ui.row, borderBottom: idx === filteredRows.length - 1 ? 'none' : ui.row.borderBottom }}>
            <div style={ui.rowLeft}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <div style={ui.num} title={inv.number}>{inv.number || `Invoice #${inv.id}`}</div>
                <div style={ui.amount}>{formatINR(inv.total)}</div>
              </div>
              <div style={ui.meta}>
                {(() => {
                  const status = String(inv?.status || '').toUpperCase() || '-';
                  const method = String(inv?.paymentMethod || '').toUpperCase();
                  const label = status === 'PAID' && method ? method : status;
                  return <span style={ui.badge(status)}>{label}</span>;
                })()}
                <span> • </span>
                <span title={inv.customerSnapshot?.name || ''}>{inv.customerSnapshot?.name || 'Customer'}</span>
                <span> • </span>
                <span>{inv.date || '-'}</span>
               <br/>
                <span title={(() => {
                  const u = inv?.createdByUser;
                  if (u?.firstName || u?.lastName) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
                  if (u?.email) return u.email;
                  const mapped = inv?.createdBy != null ? companyUsersById[String(inv.createdBy)] : '';
                  if (mapped) return mapped;
                  return inv?.createdBy ? `User #${inv.createdBy}` : '-';
                })()}>
                  Billed by{' '}
                  {(() => {
                    const u = inv?.createdByUser;
                    const name = (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
                    if (name) return name;
                    if (u?.email) return u.email;
                    const mapped = inv?.createdBy != null ? companyUsersById[String(inv.createdBy)] : '';
                    if (mapped) return mapped;
                    if (inv?.createdBy) return `User #${inv.createdBy}`;
                    return '-';
                  })()}
                </span>
              </div>
            </div>

            <div style={ui.rowRight}>
              <button type="button" style={ui.iconBtn} title="Open Invoice PDF" onClick={() => openPdf(inv)} disabled={!!actionLoading[`pdf:${inv.id}`]}>
                <FiFileText size={14} color="#374151" stroke="#374151" style={{ display: 'block' }} />
              </button>
              <button type="button" style={ui.iconBtn} title="Print POS Bill" onClick={() => onPrintPosBill && onPrintPosBill(inv)}>
                <FiPrinter size={14} color="#374151" stroke="#374151" style={{ display: 'block' }} />
              </button>
              {String(inv.status || '').toUpperCase() !== 'PAID' && (
                <button
                  type="button"
                  style={{ ...ui.iconBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 700 }}
                  onClick={() => setPayModal({ open: true, id: inv.id })}
                >
                  Mark paid
                </button>
              )}
            </div>
          </div>
        ))}

        {!loading && filteredRows.length === 0 && (
          <div style={{ padding: 12, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
            {error ? error : 'No bills found for this range.'}
          </div>
        )}

        {loading && (
          <div style={{ padding: 12, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
            Loading…
          </div>
        )}
      </div>

      <div style={ui.footer}>
        <div style={ui.smallText}>
          Showing {rows.length} of {total}
        </div>
        <button
          type="button"
          style={{ ...ui.iconBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 700, opacity: hasMore ? 1 : 0.5 }}
          onClick={() => setPage((p) => p + 1)}
          disabled={!hasMore || loading}
        >
          Load more
        </button>
      </div>

      <PaymentModal
        isOpen={payModal.open}
        onClose={() => setPayModal({ open: false, id: null })}
        onSubmit={submitMarkPaid}
        title="Mark Invoice as Paid"
        submitText="Mark as Paid"
        submitLoadingText="Saving..."
        isSubmitting={paySubmitting}
      />
    </div>
  );
}

BillingHistoryPanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refreshKey: PropTypes.number,
  onPrintPosBill: PropTypes.func,
  styles: PropTypes.object
};

